import { Popconfirm } from 'antd';
import ITVTable from 'components/ITVTable';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Container } from './styled';
import PageHeader from 'components/PageHeader';
import { useAppSelector } from 'app/hooks';
import { selectPerformanceData } from 'features/performance/performanceSlice';
import {
  deleteAssessmentForm,
  getListAssessmentEmployee,
} from 'features/performance/performanceAction';
import { getUser } from 'utils/auth';
import ButtonBack from 'components/ButtonBack';
import { useTranslation } from 'react-i18next';
import { TitleColumnCenter } from 'styles/tableStyled';
import { notificationToast } from 'components/notificationToast';

interface PerformanceReviewLocationState {
  full_name?: string;
  fullName?: string;
}

const PerformanceReviewSelf = ({ isStaff }: { isStaff: boolean }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { id }: any = useParams();
  const stateURL = (location.state || {}) as PerformanceReviewLocationState;

  const { t } = useTranslation(['employee', 'performance']);

  const performanceStore = useAppSelector(selectPerformanceData);
  const listAssessmentEmployee = useMemo(() => {
    return (performanceStore?.listAssessmentEmployee?.result as any[]) || [];
  }, [performanceStore?.listAssessmentEmployee?.result]);
  const maxNumberReviewer = performanceStore?.listAssessmentEmployee?.maxNumberReviewer || 0;
  const [sizePage, setSizePage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(getListAssessmentEmployee({ userId: id ? id : getUser()?.id }));
  }, [dispatch, id]);

  const resolveEmployeeId = (record: any) => {
    if (record?.employeeId) return record?.employeeId;
    const firstReviewer = record?.reviewers?.[0];
    if (Array.isArray(firstReviewer?.userId) && firstReviewer?.userId?.length > 0)
      return firstReviewer?.userId?.[0];
    if (firstReviewer?.userId) return firstReviewer?.userId;
    if (record?.userId) return record?.userId;
    return id ? id : getUser()?.id;
  };

  const handleDeleteReviewCycle = (reviewCycleId: string, assessmentId: string) => {
    dispatch(
      deleteAssessmentForm({
        reviewCycleId: reviewCycleId,
        assessmentId: assessmentId,
        callBack: () => {
          notificationToast('success', t('performance:delete_review_cycle_success'), 3);
          dispatch(getListAssessmentEmployee({ userId: id ? id : getUser()?.id }));
        },
      }),
    );
  };

  // Generate reviewer columns based on maxNumberReviewer
  const reviewerColumns: any[] = [];
  for (let i = 0; i < maxNumberReviewer; i++) {
    reviewerColumns.push({
      title: `Reviewer ${i + 1}`,
      key: `reviewer_${i}`,
      dataIndex: `reviewer_${i}`,
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: string | undefined) => {
        return text || '_';
      },
    });
  }

  const columns = [
    {
      title: () => {
        return <TitleColumnCenter>{t('employee:performance_review.col_id')}</TitleColumnCenter>;
      },
      key: 'employeeCode',
      dataIndex: 'employeeCode',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (_text: any, _record: any, index: number) => {
        const trueIndex = index + sizePage * (currentPage - 1);
        return <div>{trueIndex + 1}</div>;
      },
    },
    {
      title: () => {
        return (
          <TitleColumnCenter>
            {t('employee:performance_review.review_cycle_name')}
          </TitleColumnCenter>
        );
      },
      key: 'reviewCycleName',
      dataIndex: 'reviewCycleName',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: any) => {
        return text;
      },
    },
    ...reviewerColumns,
    {
      title: () => {
        return <TitleColumnCenter>{t('employee:performance_review.status')}</TitleColumnCenter>;
      },
      key: 'assessmentStatus',
      dataIndex: 'assessmentStatus',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: any) => {
        return text;
      },
    },
    {
      title: () => {
        return (
          <TitleColumnCenter>{t('employee:performance_review.overall_score')}</TitleColumnCenter>
        );
      },
      key: 'overallScore',
      dataIndex: 'overallScore',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: any) => {
        return typeof text === 'number' ? text : '_';
      },
    },
    {
      title: () => {
        return <TitleColumnCenter>{t('employee:performance_review.action')}</TitleColumnCenter>;
      },
      key: 'action',
      width: 100,
      minWidth: 100,
      align: 'center',
      fixed: 'right',
      render: (text: any, record: any) => (
        <>
          <p
            style={{ color: 'blue', cursor: 'pointer', marginBottom: 0 }}
            onClick={() => {
              const employeeId = resolveEmployeeId(record);
              const reviewCycleId = record?.revieCycleId || record?.reviewCycleId;
              const assessmentId = record?.assessmentId;
              if (!employeeId) return;
              const basePath = isStaff
                ? '/my-page/performance-review/detail'
                : '/performance/employee-pr-management';
              const pathname = reviewCycleId
                ? `${basePath}/${employeeId}/${reviewCycleId}`
                : `${basePath}/${employeeId}`;
              history.push({
                pathname,
                state: {
                  employeeId,
                  reviewCycleId,
                  assessmentId,
                  full_name: record?.full_name || record?.fullName,
                  fullName: record?.fullName || record?.full_name,
                },
              });
            }}
          >
            {t('performance:view_evaluate')}
          </p>
          {!isStaff && (
            <Popconfirm
              title={t('performance:delete_review_cycle_confirm')}
              onConfirm={() => handleDeleteReviewCycle(record?.revieCycleId, record?.assessmentId)}
              okText={t('performance:confirm')}
              cancelText={t('performance:cancel')}
            >
              <p style={{ color: 'red', cursor: 'pointer', marginBottom: 0 }}>
                {t('performance:delete')}
              </p>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  // Transform the data to match the columns
  const transformedData = listAssessmentEmployee.map((item: any) => {
    const reviewers: { [key: string]: string } = {};
    for (let i = 0; i < maxNumberReviewer; i++) {
      reviewers[`reviewer_${i}`] = item.reviewers[i] ? item.reviewers[i].userName : '_';
    }
    const employeeId = resolveEmployeeId(item);
    return {
      ...item,
      ...(employeeId ? { employeeId } : {}),
      ...reviewers,
    };
  });

  return (
    <Container>
      {id && <ButtonBack handleBack={() => history.goBack()} />}
      <PageHeader title={`${stateURL?.full_name || ''}`} />
      <ITVTable
        data={transformedData}
        columns={columns}
        isRowSelect={false}
        setOffset={setCurrentPage}
        offset={currentPage}
        setLimit={setSizePage}
        limit={sizePage}
        totalResult={performanceStore?.listAssessmentEmployee?.totalResult || undefined}
      />
    </Container>
  );
};

export default PerformanceReviewSelf;
